import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { MdOutlineSearch, MdOutlinePeopleAlt, MdChevronLeft, MdChevronRight } from "react-icons/md";
import { _getChatbotUsers, _organizationDetails } from "Services/api";
import SectionCard from "components/card/SectionCard";
import StatusBadge from "components/badge/StatusBadge";
import AvatarInitials from "components/badge/AvatarInitials";

const getPayloadData = (response) =>
  response?.data?.data || response?.data?.organization || response?.data || response || {};

const getOrganizationId = (organization) =>
  organization?.id || organization?.organization_id || organization?.org_id || organization?.uuid;

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const SearchInput = ({ value, onChange, placeholder }) => (
  <div className="relative">
    <MdOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-lg text-gray-400" />
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="h-11 w-full rounded-xl border border-gray-200 bg-white/0 pl-10 pr-3 text-sm text-navy-700 outline-none transition focus:border-brand-500 dark:border-white/10 dark:text-white"
    />
  </div>
);

const LabelledInput = ({ label, ...rest }) => (
  <div>
    <label className="mb-1.5 ml-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </label>
    <SearchInput {...rest} />
  </div>
);

const TableSkeletonRow = ({ columns }) => (
  <tr className="border-b border-gray-100 dark:border-white/5">
    {Array.from({ length: columns }).map((_, index) => (
      <td key={index} className="p-4">
        <div className="h-3.5 w-full max-w-[140px] animate-pulse rounded-full bg-gray-100 dark:bg-white/10" />
      </td>
    ))}
  </tr>
);

const columns = ["User", "Email", "Phone", "Booking User ID", "Status", "Joined"];

const ChatbotUsers = () => {
  const accessToken = sessionStorage.getItem("VendorToken");
  const isAdmin = sessionStorage.getItem("isAdmin") === "true";
  const [users, setUsers] = useState([]);
  const [pageInfo, setPageInfo] = useState({ page: 1, totalPages: 1, total: 0 });
  const [search, setSearch] = useState("");
  const [botId, setBotId] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [vendorOrganizationId, setVendorOrganizationId] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadVendorOrganization = async () => {
      if (isAdmin || !accessToken) return;

      try {
        const response = await _organizationDetails(accessToken);
        const organization = getPayloadData(response);
        setVendorOrganizationId(getOrganizationId(organization) || "");
      } catch (error) {
        toast.error(error.response?.data?.message || "Could not load vendor organization.");
      }
    };

    loadVendorOrganization();
  }, [accessToken, isAdmin]);

  const fetchUsers = useCallback(async () => {
    if (!accessToken) return;

    setLoading(true);
    try {
      const response = await _getChatbotUsers({
        accessToken,
        page: pageInfo.page,
        limit: 20,
        search,
        botId,
        organizationId: isAdmin ? organizationId : vendorOrganizationId,
      });
      const payload = getPayloadData(response);

      setUsers(payload.users || []);
      setPageInfo({
        page: payload.page || 1,
        totalPages: payload.total_pages || 1,
        total: payload.total || 0,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Could not load chatbot users.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, botId, isAdmin, organizationId, pageInfo.page, search, vendorOrganizationId]);

  useEffect(() => {
    const timeoutId = window.setTimeout(fetchUsers, 350);
    return () => window.clearTimeout(timeoutId);
  }, [fetchUsers]);

  const handleNextPage = () => {
    if (pageInfo.page < pageInfo.totalPages) {
      setPageInfo((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const handlePrevPage = () => {
    if (pageInfo.page > 1) {
      setPageInfo((prev) => ({ ...prev, page: prev.page - 1 }));
    }
  };

  const activeCount = useMemo(() => users.filter((user) => user.is_active).length, [users]);

  return (
    <div className="flex w-full flex-col gap-5">
      <SectionCard
        title="Chatbot Users"
        description={
          isAdmin
            ? "View chatbot users across organizations and bots."
            : "View chatbot users for your organization."
        }
        icon={<MdOutlinePeopleAlt />}
        actions={
          <div className="flex items-center gap-4 rounded-xl bg-lightPrimary px-4 py-2 dark:bg-navy-700">
            <div className="text-center">
              <p className="text-lg font-bold text-navy-700 dark:text-white">{pageInfo.total}</p>
              <p className="text-[11px] font-medium text-gray-500">Total Users</p>
            </div>
            <div className="h-8 w-px bg-gray-200 dark:bg-white/10" />
            <div className="text-center">
              <p className="text-lg font-bold text-navy-700 dark:text-white">{activeCount}</p>
              <p className="text-[11px] font-medium text-gray-500">Active (page)</p>
            </div>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <LabelledInput
            label="Search"
            placeholder="Search by name, email or phone"
            value={search}
            onChange={(event) => {
              setPageInfo((prev) => ({ ...prev, page: 1 }));
              setSearch(event.target.value);
            }}
          />
          <LabelledInput
            label="Bot ID"
            placeholder="Filter by bot ID"
            value={botId}
            onChange={(event) => {
              setPageInfo((prev) => ({ ...prev, page: 1 }));
              setBotId(event.target.value);
            }}
          />
          {isAdmin ? (
            <LabelledInput
              label="Organization ID"
              placeholder="Filter by organization ID"
              value={organizationId}
              onChange={(event) => {
                setPageInfo((prev) => ({ ...prev, page: 1 }));
                setOrganizationId(event.target.value);
              }}
            />
          ) : (
            <div>
              <label className="mb-1.5 ml-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Organization ID
              </label>
              <input
                type="text"
                value={vendorOrganizationId}
                disabled
                className="h-11 w-full rounded-xl border-none bg-gray-100 px-3 text-sm text-gray-500 outline-none dark:bg-white/5"
              />
            </div>
          )}
        </div>

        <div className="mt-6 overflow-x-auto rounded-xl border border-gray-100 dark:border-white/10">
          <table className="w-full min-w-max table-auto text-left">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60 dark:border-white/10 dark:bg-white/5">
                {columns.map((column) => (
                  <th key={column} className="p-4">
                    <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                      {column}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <TableSkeletonRow key={index} columns={columns.length} />
                ))
              ) : users.length ? (
                users.map((user) => {
                  const fullName =
                    [user.first_name, user.last_name].filter(Boolean).join(" ") || "Guest User";
                  return (
                    <tr
                      key={user.id || user.user_id}
                      className="border-b border-gray-100 transition hover:bg-lightPrimary/60 dark:border-white/5 dark:hover:bg-white/5"
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <AvatarInitials name={fullName} size="h-9 w-9" />
                          <span className="text-sm font-semibold text-navy-700 dark:text-white">
                            {fullName}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {user.email || "N/A"}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {user.phone_number || "N/A"}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {user.booking_user_id || "N/A"}
                        </span>
                      </td>
                      <td className="p-4">
                        <StatusBadge tone={user.is_active ? "success" : "neutral"}>
                          {user.is_active ? "Active" : "Inactive"}
                        </StatusBadge>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {formatDate(user.created_at)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="p-10 text-center" colSpan={columns.length}>
                    <div className="flex flex-col items-center gap-2">
                      <MdOutlinePeopleAlt className="text-4xl text-gray-300" />
                      <p className="text-sm font-medium text-gray-500">No chatbot users found.</p>
                      <p className="text-xs text-gray-400">Try adjusting your search or filters.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
          <p className="text-sm font-medium text-gray-500">
            Page {pageInfo.page} of {pageInfo.totalPages} &bull; {pageInfo.total} users
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handlePrevPage}
              disabled={pageInfo.page === 1}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
            >
              <MdChevronLeft /> Previous
            </button>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={pageInfo.page === pageInfo.totalPages}
              className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
            >
              Next <MdChevronRight />
            </button>
          </div>
        </div>
      </SectionCard>
    </div>
  );
};

export default ChatbotUsers;
